import { apiGet, apiPut, apiPost, apiPatch, apiDelete, ApiError } from './client';
import { authHeaders } from '@/auth/tokenInterceptor';
import type {
  ApplyRequestDto,
  ApplyResponseDto,
  CompareResponseDto,
  RevealResponseDto,
  RestoreVerifyResponseDto,
  RestoreApplyResponseDto,
  AddEnvironmentRequest,
  AddGroupRequest,
  AddProjectRequest,
  AddServiceRequest,
  ApiKeyDto,
  AuditPageDto,
  ConfigFileDto,
  CreateApiKeyRequest,
  CreateApiKeyResponse,
  CurrentUserDto,
  EnvironmentDto,
  FileSnapshotDto,
  FileSnapshotSummaryDto,
  GroupTreeDto,
  LayerDetailDto,
  ProjectTreeDto,
  MergedPreviewDto,
  ReorderEnvironmentsRequest,
  SaveConfigFileRequest,
  SecretSummaryDto,
  SecretVersionDto,
  SecretVersionSummaryDto,
  SecretWithValueDto,
  ConfigurationInfoDto,
  ServerInfoDto,
  ServiceDetailDto,
  ServiceSummaryDto,
  SetArchivedRequest,
  UpdateApiKeyRequest,
  UpdateMetadataRequest,
  ValidationResultDto,
} from './types';

const BASE = '/api/admin';

// SecretReference encoding: ':' -> '~' so the segment is path-safe (some proxies reject ':' in
// path segments). Matches SecretReferenceUrlEncoder.TryDecode on the server. The reference is
// now two-segment (Scope:Name) — the Slot is a separate path segment.
export const encodeReference = (scopeColonName: string): string =>
  encodeURIComponent(scopeColonName.replace(/:/g, '~'));

interface ReloadResponse {
  success: boolean;
  host: string;
}

export const adminApi = {
  me: () => apiGet<CurrentUserDto>(`${BASE}/me`),

  groups: () => apiGet<GroupTreeDto[]>(`${BASE}/groups`),

  addGroup: (request: AddGroupRequest) =>
    apiPost<GroupTreeDto>(`${BASE}/groups`, request),

  addProject: (group: string, request: AddProjectRequest) =>
    apiPost<ProjectTreeDto>(`${BASE}/groups/${encodeURIComponent(group)}/projects`, request),

  setGroupArchived: (group: string, request: SetArchivedRequest) =>
    apiPut<void>(`${BASE}/groups/${encodeURIComponent(group)}/archived`, request),

  setProjectArchived: (group: string, project: string, request: SetArchivedRequest) =>
    apiPut<void>(
      `${BASE}/groups/${encodeURIComponent(group)}/projects/${encodeURIComponent(project)}/archived`,
      request,
    ),

  // Server-side cache invalidation + composer preload. Only invalidates this replica.
  reload: () => apiPost<ReloadResponse>(`${BASE}/reload`, {}),

  getFile: (path: string) =>
    apiGet<ConfigFileDto>(`${BASE}/files?path=${encodeURIComponent(path)}`),

  saveFile: (request: SaveConfigFileRequest) =>
    apiPut<ConfigFileDto>(`${BASE}/files`, request),

  validateFile: (request: SaveConfigFileRequest) =>
    apiPost<ValidationResultDto>(`${BASE}/files/validate`, request),

  // Per-file version history. Snapshots are captured automatically on every save that
  // changes the file's content; `.history/` lives next to the source file on disk.
  listFileHistory: (path: string) =>
    apiGet<FileSnapshotSummaryDto[]>(`${BASE}/files/history?path=${encodeURIComponent(path)}`),

  getFileVersion: (path: string, stamp: string) =>
    apiGet<FileSnapshotDto>(
      `${BASE}/files/history/version?path=${encodeURIComponent(path)}&stamp=${encodeURIComponent(stamp)}`,
    ),

  preview: (group: string, project: string, env: string) =>
    apiGet<MergedPreviewDto>(
      `${BASE}/preview/${encodeURIComponent(group)}/${encodeURIComponent(project)}/${encodeURIComponent(env)}`,
    ),

  // Slot is "_base" or an Environment name. Scope filter narrows by the first reference segment.
  listSecrets: (slot?: string, scope?: string) => {
    const qs = new URLSearchParams();
    if (slot) qs.set('slot', slot);
    if (scope) qs.set('scope', scope);
    const tail = qs.toString();
    return apiGet<SecretSummaryDto[]>(`${BASE}/secrets${tail ? `?${tail}` : ''}`);
  },

  getSecret: (slot: string, scopeColonName: string) =>
    apiGet<SecretWithValueDto>(
      `${BASE}/secrets/${encodeURIComponent(slot)}/${encodeReference(scopeColonName)}`,
    ),

  setSecret: (slot: string, scopeColonName: string, value: string) =>
    apiPut<void>(
      `${BASE}/secrets/${encodeURIComponent(slot)}/${encodeReference(scopeColonName)}`,
      { value },
    ),

  deleteSecret: (slot: string, scopeColonName: string) =>
    apiDelete(`${BASE}/secrets/${encodeURIComponent(slot)}/${encodeReference(scopeColonName)}`),

  // Whole-secret delete: removes every slot's entry for the reference (including _base).
  // The per-slot deleteSecret rejects _base on its own; this is the only way to dissolve a
  // secret completely.
  deleteSecretReference: (scopeColonName: string) =>
    apiDelete(`${BASE}/secrets/${encodeReference(scopeColonName)}`),

  // Per-slot version history. EncryptedFile backend keeps a per-entry history array inside
  // the encrypted blob; Key Vault delegates to its native version API. The current head is
  // included in the list with IsCurrent=true.
  listSecretVersions: (slot: string, scopeColonName: string) =>
    apiGet<SecretVersionSummaryDto[]>(
      `${BASE}/secrets/${encodeURIComponent(slot)}/${encodeReference(scopeColonName)}/versions`,
    ),

  getSecretVersion: (slot: string, scopeColonName: string, versionId: string) =>
    apiGet<SecretVersionDto>(
      `${BASE}/secrets/${encodeURIComponent(slot)}/${encodeReference(scopeColonName)}/versions/${encodeURIComponent(versionId)}`,
    ),

  // API keys (bearer tokens that gate /api/configuration/*). Stored as argon2id hashes —
  // see docs/config-server/api-keys.md. The CreateApiKey response is the ONLY moment the
  // plaintext value exists outside the client that received it; nothing persists it locally.
  listApiKeys: () => apiGet<ApiKeyDto[]>(`${BASE}/api-keys`),

  createApiKey: (request: CreateApiKeyRequest) =>
    apiPost<CreateApiKeyResponse>(`${BASE}/api-keys`, request),

  updateApiKey: (id: string, request: UpdateApiKeyRequest) =>
    apiPatch<ApiKeyDto>(`${BASE}/api-keys/${encodeURIComponent(id)}`, request),

  disableApiKey: (id: string) =>
    apiPost<void>(`${BASE}/api-keys/${encodeURIComponent(id)}/disable`, {}),

  enableApiKey: (id: string) =>
    apiPost<void>(`${BASE}/api-keys/${encodeURIComponent(id)}/enable`, {}),

  deleteApiKey: (id: string) =>
    apiDelete(`${BASE}/api-keys/${encodeURIComponent(id)}`),

  // Environment registry CRUD. Add cascades empty layer files; remove is soft (registry only).
  listEnvironments: () => apiGet<EnvironmentDto[]>(`${BASE}/environments`),

  addEnvironment: (request: AddEnvironmentRequest) =>
    apiPost<EnvironmentDto>(`${BASE}/environments`, request),

  removeEnvironment: (name: string) =>
    apiDelete(`${BASE}/environments/${encodeURIComponent(name)}`),

  reorderEnvironments: (request: ReorderEnvironmentsRequest) =>
    apiPut<void>(`${BASE}/environments/order`, request),

  // Services CRUD. Add cascades empty layer files for every registered env. Remove is a hard
  // delete (recovery is git-only); the SPA gates it behind a typed-confirmation modal. Per-
  // slot content edits go through the existing /api/admin/files endpoints with the path
  // services/{name}/{slot}.json.
  listServices: () => apiGet<ServiceSummaryDto[]>(`${BASE}/services`),

  getService: (name: string) =>
    apiGet<ServiceDetailDto>(`${BASE}/services/${encodeURIComponent(name)}`),

  addService: (request: AddServiceRequest) =>
    apiPost<ServiceSummaryDto>(`${BASE}/services`, request),

  removeService: (name: string) =>
    apiDelete(`${BASE}/services/${encodeURIComponent(name)}`),

  // Group layer-detail (slot-fill flags for the LayeredSlotEditor pill row).
  // Read-only; per-slot content edits ride /api/admin/files like every other layer.
  getGroupLayers: (group: string) =>
    apiGet<LayerDetailDto>(`${BASE}/groups/${encodeURIComponent(group)}/layers`),

  // Entity metadata PUTs. Reads are bundled into the catalog response (GroupTreeDto,
  // ProjectTreeDto, ServiceSummaryDto carry displayName/description/category) so there's no
  // matching GET — callers read from the catalog store and POST diffs back here.
  updateGroupMetadata: (group: string, request: UpdateMetadataRequest) =>
    apiPut<void>(`${BASE}/groups/${encodeURIComponent(group)}/meta`, request),

  updateProjectMetadata: (group: string, project: string, request: UpdateMetadataRequest) =>
    apiPut<void>(
      `${BASE}/groups/${encodeURIComponent(group)}/projects/${encodeURIComponent(project)}/meta`,
      request,
    ),

  updateServiceMetadata: (name: string, request: UpdateMetadataRequest) =>
    apiPut<void>(`${BASE}/services/${encodeURIComponent(name)}/meta`, request),

  // GET /api/admin/audit — server-side filter + pagination. All filters are optional and
  // resolved server-side as case-insensitive substring matches; since/until clip the time
  // range. Returns a paged AuditPageDto whose `totalCount` drives the SPA's paginator.
  audit: (params: {
    actor?: string;
    action?: string;
    target?: string;
    since?: string;
    until?: string;
    page?: number;
    pageSize?: number;
  }) => {
    const qs = new URLSearchParams();
    if (params.actor) qs.set('actor', params.actor);
    if (params.action) qs.set('action', params.action);
    if (params.target) qs.set('target', params.target);
    if (params.since) qs.set('since', params.since);
    if (params.until) qs.set('until', params.until);
    if (params.page !== undefined) qs.set('page', String(params.page));
    if (params.pageSize !== undefined) qs.set('pageSize', String(params.pageSize));
    const tail = qs.toString();
    return apiGet<AuditPageDto>(`${BASE}/audit${tail ? `?${tail}` : ''}`);
  },
};

// Backup/restore needs blob download + multipart upload, which the JSON client doesn't
// model — these use fetch directly with the same auth headers. The password and source
// master key are sent in the request body only and never logged or persisted client-side.
export const systemApi = {
  // Read-only server diagnostics (incl. the non-reversible master-key mask + fingerprint).
  info: () => apiGet<ServerInfoDto>(`${BASE}/server-info`),
  // The merged IConfiguration visible to the .NET host (appsettings + env +
  // every registered provider). AdminUser-gated; access is audited.
  configuration: () => apiGet<ConfigurationInfoDto>(`${BASE}/server-info/configuration`),
};

export const backupApi = {
  download: async (password: string): Promise<{ blob: Blob; filename: string }> => {
    const auth = await authHeaders();
    const response = await fetch(`${BASE}/backup`, {
      method: 'POST',
      headers: { ...auth, 'Content-Type': 'application/json' },
      body: JSON.stringify({ password }),
    });
    if (!response.ok) {
      const text = await response.text();
      throw new ApiError(response.status, `${response.status} ${response.statusText}`, text);
    }
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match ? decodeURIComponent(match[1]) : 'config-server-backup.csbak';
    return { blob: await response.blob(), filename };
  },

  // ---- Selective restore (D1, D12). The four routes share one multipart pattern. ----

  compare: async (
    file: File,
    password: string,
    sourceMasterKey?: string,
    sourceMasterKeyApiKeys?: string,
  ): Promise<CompareResponseDto> =>
    postRestore<CompareResponseDto>('/backup/restore/compare', file, password, sourceMasterKey, {
      ...(sourceMasterKeyApiKeys ? { sourceMasterKeyApiKeys } : {}),
    }),

  apply: async (
    file: File,
    password: string,
    request: ApplyRequestDto,
    sourceMasterKey?: string,
    sourceMasterKeyApiKeys?: string,
  ): Promise<ApplyResponseDto> =>
    postRestore<ApplyResponseDto>('/backup/restore/apply', file, password, sourceMasterKey, {
      confirmationToken: request.confirmationToken,
      selection: JSON.stringify(request.selection),
      serverVersionAcknowledged: request.serverVersionAcknowledged ? 'true' : 'false',
      ...(sourceMasterKeyApiKeys ? { sourceMasterKeyApiKeys } : {}),
    }),

  retryFailed: async (
    file: File,
    password: string,
    request: ApplyRequestDto,
    sourceMasterKey?: string,
    sourceMasterKeyApiKeys?: string,
  ): Promise<ApplyResponseDto> =>
    postRestore<ApplyResponseDto>('/backup/restore/retry-failed', file, password, sourceMasterKey, {
      confirmationToken: request.confirmationToken,
      selection: JSON.stringify(request.selection),
      serverVersionAcknowledged: request.serverVersionAcknowledged ? 'true' : 'false',
      ...(sourceMasterKeyApiKeys ? { sourceMasterKeyApiKeys } : {}),
    }),

  reveal: async (
    file: File,
    password: string,
    confirmationToken: string,
    rowKey: string,
    sourceMasterKey?: string,
    sourceMasterKeyApiKeys?: string,
  ): Promise<RevealResponseDto> =>
    postRestore<RevealResponseDto>('/backup/restore/reveal', file, password, sourceMasterKey, {
      confirmationToken,
      rowKey,
      ...(sourceMasterKeyApiKeys ? { sourceMasterKeyApiKeys } : {}),
    }),

  // ---- Legacy verify+apply, retained until the new flow ships in the UI (T9 cleanup). ----

  verifyRestore: async (
    file: File,
    password: string,
    sourceMasterKey?: string,
  ): Promise<RestoreVerifyResponseDto> =>
    postRestore<RestoreVerifyResponseDto>('/backup/restore/verify', file, password, sourceMasterKey),

  applyRestore: async (
    file: File,
    password: string,
    confirmationToken: string,
    sourceMasterKey?: string,
    serverVersionAcknowledged?: boolean,
  ): Promise<RestoreApplyResponseDto> =>
    postRestore<RestoreApplyResponseDto>('/backup/restore/apply', file, password, sourceMasterKey, {
      confirmationToken,
      serverVersionAcknowledged: serverVersionAcknowledged ? 'true' : 'false',
    }),
};

async function postRestore<T>(
  path: string,
  file: File,
  password: string,
  sourceMasterKey?: string,
  extra?: Record<string, string>,
): Promise<T> {
  const auth = await authHeaders();
  const form = new FormData();
  form.append('file', file);
  form.append('password', password);
  if (sourceMasterKey) {
    form.append('sourceMasterKey', sourceMasterKey);
  }
  for (const [k, v] of Object.entries(extra ?? {})) {
    form.append(k, v);
  }
  const response = await fetch(`${BASE}${path}`, { method: 'POST', headers: { ...auth }, body: form });
  const text = await response.text();
  let parsed: unknown = undefined;
  if (text.length > 0) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }
  // 409 on apply carries a structured RestoreApplyResponseDto (verification failed); surface
  // it to the caller as data rather than throwing so the wizard can render the problems.
  if (!response.ok && response.status !== 409) {
    throw new ApiError(response.status, `${response.status} ${response.statusText}`, parsed);
  }
  return parsed as T;
}
